import axios from "axios"
import { useLayoutEffect, useRef, useState } from "react"
import { useStoreState } from "easy-peasy"
import ErrorPopup from "./ErrorPopup.js"
import StatusPopup from "./StatusPopup.js"
import { DMV_SEARCH_URL, DMV_RECORD_URL } from "./Constants"

// The six searchable fields (server caps an actual query at 3).
const FIELD_OPTIONS = [
	{ value: "name", label: "Owner Name" },
	{ value: "address", label: "Street Address" },
	{ value: "data_year", label: "Data Year" },
	{ value: "model_year", label: "Model Year" },
	{ value: "make", label: "Make" },
	{ value: "vin_hin", label: "VIN / HIN" },
]

const TYPE_LABEL = { vehicle: "Vehicle", boat: "Boat", mobile_home: "Mobile Home" }

// Labeled, ordered view of a full record for the drill-down modal.
const DETAIL_FIELDS = [
	["Asset Type", (r) => TYPE_LABEL[r.asset_type] || r.asset_type],
	["Data Year", (r) => r.data_year],
	["Owner", (r) => r.owner_name],
	["Street", (r) => r.street],
	["City", (r) => r.city],
	["Zip", (r) => r.zip],
	["VIN / HIN", (r) => r.vin_hin],
	["Make", (r) => r.make],
	["Model Year", (r) => r.model_year],
	["Body / Hull", (r) => r.body_hull],
	["Weight / Length", (r) => r.wgt_leng],
	["Title No", (r) => r.title_no],
	["Title Date", (r) => r.title_date],
	["Plate", (r) => r.plate],
	["Plate Class", (r) => r.plate_class],
	["Plate Exp", (r) => r.plate_exp_date],
	["Multi-Year", (r) => r.multi_yr],
]

const blankCriteria = () => [
	{ field: "name", value: "" },
	{ field: "address", value: "" },
	{ field: "data_year", value: "" },
]

const th = { textAlign: "left", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 }
const td = { whiteSpace: "nowrap" }

function fmt(v) {
	if (v === null || v === undefined || v === "") return "—"
	return String(v)
}

function DetailModal({ record, onClose }) {
	return (
		<div
			onClick={onClose}
			style={{
				position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
				display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
			}}
		>
			<div
				onClick={(e) => e.stopPropagation()}
				style={{
					backgroundColor: "#fff", color: "#000", padding: "1rem 1.5rem",
					borderRadius: "6px", maxHeight: "85vh", overflowY: "auto", minWidth: "22rem",
				}}
			>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
					<h3 style={{ margin: 0 }}>DMV Record</h3>
					<button type="button" className="saveBtn" onClick={onClose}>Close</button>
				</div>
				{DETAIL_FIELDS.map(([label, get]) => (
					<div key={label} style={{ display: "flex", gap: "0.5rem", padding: "1px 0" }}>
						<div style={{ fontWeight: 600, minWidth: "9rem", whiteSpace: "nowrap" }}>{label}:</div>
						<div>{fmt(get(record))}</div>
					</div>
				))}
			</div>
		</div>
	)
}

const Dmv = () => {
	const Token = useStoreState((state) => state.Token)

	const [criteria, setCriteria] = useState(blankCriteria)
	const [searchType, setSearchType] = useState("contains")
	const [results, setResults] = useState([])
	const [status, setStatus] = useState("") // "loading" | "empty" | ""
	const [error, setError] = useState("")
	const [detail, setDetail] = useState(null)
	const [scrollMax, setScrollMax] = useState(null)
	const scrollRef = useRef(null)

	// Size the results scroll area so the fieldset's bottom ends ~10px above the
	// fixed footer, regardless of search-form height or viewport size.
	useLayoutEffect(() => {
		if (results.length === 0) return
		function recompute() {
			const sd = scrollRef.current
			if (!sd) return
			const fieldset = sd.parentElement
			const footer = document.querySelector(".Footer")
			const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight
			// fieldset chrome (padding/border) below the scroll box, so the
			// FIELDSET edge — not just the box — lands 10px above the footer.
			const chromeBelow = fieldset.getBoundingClientRect().bottom - sd.getBoundingClientRect().bottom
			const h = Math.max(footerTop - sd.getBoundingClientRect().top - chromeBelow - 10, 120)
			setScrollMax(h)
		}
		recompute()
		window.addEventListener("resize", recompute)
		return () => window.removeEventListener("resize", recompute)
	}, [results])

	function setCriterion(i, key, val) {
		setCriteria((prev) => prev.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)))
	}

	async function handleSearch(e) {
		e.preventDefault()
		setError("")
		setResults([])
		const active = criteria
			.map((c) => ({ field: c.field, value: c.value.trim() }))
			.filter((c) => c.value)
		if (active.length === 0) {
			setError("Enter at least one search value.")
			return
		}
		setStatus("loading")
		try {
			const { data } = await axios.post(DMV_SEARCH_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				search_type: searchType,
				criteria: active,
			})
			setResults(data)
			setStatus(data.length ? "" : "empty")
		} catch (err) {
			if (err.response && err.response.status === 404) {
				setStatus("empty")
			} else {
				setStatus("")
				setError(err.response?.data?.detail || "Search failed.")
			}
		}
	}

	function handleClear() {
		setCriteria(blankCriteria())
		setSearchType("contains")
		setResults([])
		setStatus("")
		setError("")
	}

	async function openDetail(id) {
		setError("")
		try {
			const { data } = await axios.post(DMV_RECORD_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				record_id: id,
			})
			setDetail(data)
		} catch (err) {
			setError(err.response?.data?.detail || "Could not load record.")
		}
	}

	return (
		<div style={{ padding: "1rem" }}>
			<fieldset style={{ width: "fit-content" }}>
				<legend>DMV Registration Search</legend>
				<form onSubmit={handleSearch}>
					<table style={{ border: "none" }}>
						<tbody>
							{criteria.map((c, i) => (
								<tr key={i}>
									<td style={{ padding: "2px 4px" }}>
										<select value={c.field} onChange={(e) => setCriterion(i, "field", e.target.value)}>
											{FIELD_OPTIONS.map((o) => (
												<option
												key={o.value}
												value={o.value}
												disabled={criteria.some((cr, idx) => idx !== i && cr.field === o.value)}
											>
												{o.label}
											</option>
											))}
										</select>
									</td>
									<td style={{ padding: "2px 4px" }}>
										<input
											type="text"
											value={c.value}
											onChange={(e) => setCriterion(i, "value", e.target.value)}
											placeholder="value"
											style={{ width: "16rem" }}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<div style={{ marginTop: "0.5rem", display: "flex", gap: "1rem", alignItems: "center" }}>
						<span style={{ display: "inline-flex", alignItems: "center", gap: "0.75rem" }}>
							<label style={{ fontWeight: 600 }}>Search Type:</label>
							<label>
								<input type="radio" name="searchType" value="contains" checked={searchType === "contains"} onChange={() => setSearchType("contains")} /> Contains
							</label>
							<label>
								<input type="radio" name="searchType" value="similar" checked={searchType === "similar"} onChange={() => setSearchType("similar")} /> Similar
							</label>
						</span>
						<button type="submit" className="saveBtn">Search</button>
						<button type="button" className="saveBtn" onClick={handleClear}>Clear</button>
					</div>
				</form>
			</fieldset>

			{status === "loading" && <StatusPopup message="Searching..." />}
			{status === "empty" && <StatusPopup message="No records found." />}

			{results.length > 0 && (
				<fieldset style={{ marginTop: "1rem", width: "fit-content" }}>
					<legend>
						{results.length} record{results.length === 1 ? "" : "s"}
						{results.length >= 500 ? " (first 500 — refine to narrow)" : ""}
					</legend>
					<div ref={scrollRef} style={{ maxHeight: scrollMax ? `${scrollMax}px` : "60vh", overflowY: "auto" }}>
					<table className="receipts-table" style={{ width: "fit-content" }}>
						<thead>
							<tr>
								<th style={th}>Year</th>
								<th style={th}>Type</th>
								<th style={th}>Owner Name</th>
								<th style={th}>Address</th>
								<th style={th}>City</th>
								<th style={th}>Model Yr</th>
								<th style={th}>Make</th>
								<th style={th}>VIN / HIN</th>
							</tr>
						</thead>
						<tbody>
							{results.map((r) => (
								<tr
									key={r.id}
									style={{ cursor: "pointer" }}
									onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "gold")}
									onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
									onClick={() => openDetail(r.id)}
								>
									<td style={td}>{r.data_year}</td>
									<td style={td}>{TYPE_LABEL[r.type] || r.type}</td>
									<td style={td}>{r.name}</td>
									<td style={td}>{r.address}</td>
									<td style={td}>{r.city}</td>
									<td style={td}>{r.model_year}</td>
									<td style={td}>{r.make}</td>
									<td style={td}>{r.vin_hin}</td>
								</tr>
							))}
						</tbody>
					</table>
					</div>
				</fieldset>
			)}

			{detail && <DetailModal record={detail} onClose={() => setDetail(null)} />}
			<ErrorPopup message={error} onClose={() => setError("")} />
		</div>
	)
}

export default Dmv
